import {FC} from "react"
import {useTranslation} from "react-i18next";
import NumberUtils from '../utils/NumberUtils';
import {ReactComponent as ArrowIcon} from '../icons/next.svg';
import styled from 'styled-components';

const PaginationButton = styled.button`
    min-height: 3.25em;
    min-width: 3.25em;

    &.prev, &.next {
      .pagination-label {
        display: none;
      }
    }

    &.prev svg {
      transform: translateX(-0.25em) scaleX(-1)
    }
    &.next svg {
      transform: translateX(0.25em)
    }
`;

type TProps = {
    totalItems: number;
    itemsPerPage: number;
    currentPage: number;
    onPageChange: (element: HTMLElement, page: number) => void;
}

type NavigationButtonType = {
    label: string,
    page: number,
    isDisabled: boolean,
    isVisible: boolean,
}

const Pagination: FC<TProps> = ({totalItems, itemsPerPage, currentPage, onPageChange}) => {
    const { t } = useTranslation();
    const pagesToShow: number = 3;
    const pageDistance: number = (pagesToShow - 1) / 2;

    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

    const getAllPages = (): NavigationButtonType[] => {
        return pages.map(page => ({
            label: String(page),
            page: page,
            isDisabled: (currentPage === page),
            isVisible:
                // Show at least pagesToShow.
                (currentPage < pagesToShow && page <= pagesToShow)
                || (currentPage > (totalPages - pagesToShow + 1) && page > (totalPages - pagesToShow))
                // Otherwise, show if this page is in range.
                || NumberUtils.isWithinDistance(page, currentPage, pageDistance),
        }));
    }

    const navigationButtons: NavigationButtonType[] = [
        { label: 'prev', page: currentPage - 1, isDisabled: (currentPage === 1), isVisible: true },
        ...getAllPages(),
        { label: 'next', page: currentPage + 1, isDisabled: (currentPage === totalPages), isVisible: true },
    ];

    return (
        totalPages > 1 ? (
            <div className="cr-flex cr-gap cr-center cr-my-xl">
                {navigationButtons.map((button, index) => (
                    button.isVisible &&
                    <PaginationButton
                        key={index}
                        onClick={(event) => onPageChange(event.currentTarget, button.page)}
                        disabled={button.isDisabled}
                        aria-label={t(button.label)}
                        className={
                            'cr-flex cr-center cr-rounded-full cr-border cr-border-grey-medium cr-text-grey-medium '
                            + `${button.page === currentPage ? 'active cr-bg-grey-medium cr-text-white ' : 'cr-bg-white '}`
                            + `${(button.label === 'prev' || button.label === 'next') ? button.label : ''}`
                        }
                    >
                        {(button.label === 'prev' || button.label === 'next')
                            && <ArrowIcon/>
                        }
                        <span className="pagination-label">{t(button.label)}</span>
                    </PaginationButton>
                ))}
            </div>
        ): null
    );
}

export default Pagination;
